import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { css, useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import PropTypes from 'prop-types'

import { usePopupDismiss } from '../usePopupDismiss'

const MAX_TAB_WIDTH = 200

// Full-width positioning context and, crucially, the element `recompute`
// measures and the ResizeObserver watches: its clientWidth is the space
// available to the tabs. It must stay full width so recompute() sees the real
// container width on both shrink AND grow — measuring the shrink-wrapped Row
// instead would pin available width to the currently-visible tabs and strand
// hidden accounts behind "+N" when the viewport later widens.
const Root = styled.div`
  position: relative;
  width: 100%;
`

// The continuous baseline rule lives on the Row (below), not on each tab, so
// there are never gaps between tabs. The active tab draws top/left/right
// borders and masks the baseline segment beneath it (opaque bottom border +
// negative margin) to keep its open-bottom "tray" look. This mirrors the
// existing overall-strategy breakdown tabs.
const LINE_COLOR = p => p.theme.lighterGray
// Color revealed under the active tab's open bottom — i.e. the panel the tabs
// sit on. Falls back to white when the theme doesn't provide a panel color.
const PANEL_COLOR = p => p.theme.pageBackgroundColor ?? '#ffffff'

const TabButton = styled.button`
  flex: 0 1 auto;
  /* A real minimum keeps tabs from shrinking to nothing: when they can't all
     fit at this width the last one collapses to the "+X" control instead. */
  min-width: 120px;
  max-width: ${MAX_TAB_WIDTH}px;
  padding: 12px 20px;
  font-family: 'Open Sans', 'Helvetica Neue', sans-serif;
  font-size: 14px;
  line-height: 1.35;
  background: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  border: 1px solid transparent;
  transition: color 0.2s ease;
  ${p =>
    p.active
      ? css`
          color: ${p.theme.primaryColor};
          font-weight: 600;
          border-color: ${LINE_COLOR(p)} ${LINE_COLOR(p)} ${PANEL_COLOR(p)} ${LINE_COLOR(p)};
          /* Overlap the Row's 1px baseline so the opaque bottom border masks
             the line directly under the active tab, opening the tray. */
          margin-bottom: -1px;
          cursor: default;
        `
      : css`
          color: ${p.theme.mediumGray};
          cursor: pointer;
          &:hover {
            color: ${p.theme.primaryColor};
          }
        `};
`

// The "+N" overflow control is styled like an inactive tab so it sits on the
// same baseline rule. `flex: 0 0 auto` keeps it from being squeezed.
const OverflowButton = styled(TabButton)`
  flex: 0 0 auto;
  /* The "+X" control is a compact affordance, not an account name — let it size
     to its content rather than inheriting the tabs' 100px minimum. */
  min-width: auto;
  max-width: none;
  font-weight: 600;
`

// The visible tab strip shrink-wraps to the tabs it holds and centers within
// the full-width Root, so a handful of tabs don't leave a wide empty gap on the
// right. `max-width: 100%` caps it at the container width, so when there are
// enough accounts to overflow it fills the full width and recompute() (which
// measures Root, not this) collapses the extras into the "+N" control.
const Row = styled.div`
  display: flex;
  align-items: flex-end;
  width: fit-content;
  max-width: 100%;
  margin: 0 auto;
  border-bottom: 1px solid ${LINE_COLOR};
`

// Off-screen copy of every tab (plus a sample "+N") used purely to measure
// natural widths so we can decide how many fit. Kept out of the flow and
// invisible; never interactive.
const Measurer = styled(Row)`
  position: absolute;
  top: 0;
  left: 0;
  margin: 0;
  visibility: hidden;
  height: 0;
  overflow: hidden;
  pointer-events: none;
`

const Menu = styled.ul`
  position: absolute;
  right: 0;
  top: 100%;
  z-index: 20;
  margin: 4px 0 0;
  /* 5px inset so each menu item sits 5px inside the outer menu. */
  padding: 5px;
  list-style: none;
  min-width: 180px;
  max-width: 280px;
  max-height: 260px;
  overflow-y: auto;
  background: #ffffff;
  border: solid 1px ${p => p.theme.lighterGray};
  border-radius: 10px;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.5);
`

const MenuItem = styled.button`
  display: block;
  width: 100%;
  padding: 10px 16px;
  background: none;
  border: none;
  border-radius: 10px;
  text-align: left;
  font-family: 'Open Sans', 'Helvetica Neue', sans-serif;
  font-size: 14px;
  color: ${p => p.theme.mediumGray};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  &:hover {
    background: ${p => p.theme.primaryLight};
  }
`

const keyOf = account => account.id

// Move `account` to `slot` within `list`, shifting the others. Used to pull an
// account into the visible tab row (the account previously in that slot drops
// into the overflow bucket). Returns the same reference when nothing moves.
const promoteToSlot = (list, account, slot) => {
  const from = list.findIndex(a => keyOf(a) === keyOf(account))
  if (from < 0 || from === slot) {
    return list
  }
  const next = list.filter(a => keyOf(a) !== keyOf(account))
  next.splice(slot, 0, account)
  return next
}

const DesktopTabs = ({ accounts, activeId, onChange, maxVisibleTabs, className }) => {
  const theme = useTheme()
  // Display order is owned here (not by the parent): selecting a hidden account
  // reorders it into the last visible slot. Active state stays controlled.
  const [order, setOrder] = useState(accounts)
  const [visibleCount, setVisibleCount] = useState(accounts.length)
  const [menuOpen, setMenuOpen] = useState(false)

  // rootRef is both the outside-click boundary and the width-measurement
  // container: it's the full-width Root, so its clientWidth is the space the
  // tabs have available (see Root's styled comment).
  const rootRef = useRef(null)
  const measureRefs = useRef([])
  const measureOverflowRef = useRef(null)

  // Resync display order when the set of accounts changes (by id set), so
  // added/removed accounts show up without stomping an in-progress reorder.
  useEffect(() => {
    setOrder(prev => {
      const sameSet =
        prev.length === accounts.length && prev.every(p => accounts.some(a => a.id === p.id))
      return sameSet ? prev : accounts
    })
  }, [accounts])

  // Keep the controlled active account inside the visible window. If it starts
  // in the overflow bucket (parent selected a hidden account) or a resize
  // shrinks visibleCount so it falls past the visible slice, promote it into
  // the right-most visible slot so the active tab is always shown.
  useEffect(() => {
    if (visibleCount >= order.length) {
      return
    }
    const activeIndex = order.findIndex(a => a.id === activeId)
    if (activeIndex < 0 || activeIndex < visibleCount) {
      return
    }
    const slot = Math.max(0, visibleCount - 1)
    setOrder(prev => {
      const account = prev.find(a => a.id === activeId)
      return account ? promoteToSlot(prev, account, slot) : prev
    })
  }, [activeId, visibleCount, order])

  const recompute = useCallback(() => {
    if (maxVisibleTabs != null) {
      setVisibleCount(Math.min(maxVisibleTabs, order.length))
      return
    }
    const container = rootRef.current
    const available = container?.clientWidth ?? 0
    const widths = measureRefs.current.map(el => el?.getBoundingClientRect().width ?? 0)
    const overflowWidth = measureOverflowRef.current?.getBoundingClientRect().width ?? 0

    // No layout available yet (e.g. jsdom, or pre-paint): show everything and
    // let a later measurement pass collapse tabs if needed.
    if (!available || widths.length !== order.length || widths.some(w => !w)) {
      setVisibleCount(order.length)
      return
    }

    const total = widths.reduce((sum, w) => sum + w, 0)
    if (total <= available) {
      setVisibleCount(order.length)
      return
    }

    // Overflow is needed, so reserve room for the "+N" control up front.
    let used = overflowWidth
    let count = 0
    for (const w of widths) {
      if (used + w <= available) {
        used += w
        count += 1
      } else {
        break
      }
    }
    setVisibleCount(Math.max(1, count))
  }, [order, maxVisibleTabs])

  // Measure after every layout-affecting render.
  useLayoutEffect(() => {
    recompute()
  }, [recompute])

  // Recompute on container resize. Watching the full-width Root (not the
  // shrink-wrapped Row) is what lets a widening viewport bring hidden accounts
  // back out of the "+N" menu.
  useEffect(() => {
    const container = rootRef.current
    if (!container || typeof ResizeObserver === 'undefined') {
      return undefined
    }
    const observer = new ResizeObserver(() => recompute())
    observer.observe(container)
    return () => observer.disconnect()
  }, [recompute])

  usePopupDismiss(rootRef, () => setMenuOpen(false), menuOpen)

  const hasOverflow = visibleCount < order.length
  const visible = hasOverflow ? order.slice(0, visibleCount) : order
  const overflow = hasOverflow ? order.slice(visibleCount) : []
  const hiddenCount = overflow.length

  // Promote a hidden account into the right-most visible slot and make it
  // active. The account previously in that slot drops into the overflow menu.
  const selectFromOverflow = account => {
    const slot = Math.max(0, visibleCount - 1)
    setOrder(prev => promoteToSlot(prev, account, slot))
    onChange(account)
    setMenuOpen(false)
  }

  return (
    <Root ref={rootRef} className={className}>
      <Row>
        {visible.map(account => (
          <TabButton
            key={keyOf(account)}
            type='button'
            active={account.id === activeId}
            theme={theme}
            title={account.name}
            aria-current={account.id === activeId ? 'true' : undefined}
            onClick={() => account.id !== activeId && onChange(account)}>
            {account.name}
          </TabButton>
        ))}

        {hasOverflow && (
          <OverflowButton
            type='button'
            theme={theme}
            aria-haspopup='menu'
            aria-expanded={menuOpen}
            aria-label={`Show ${hiddenCount} more account${hiddenCount === 1 ? '' : 's'}`}
            onClick={() => setMenuOpen(open => !open)}>
            +{hiddenCount}
          </OverflowButton>
        )}
      </Row>

      {hasOverflow && menuOpen && (
        <Menu role='menu'>
          {overflow.map(account => (
            <li key={keyOf(account)}>
              <MenuItem
                type='button'
                role='menuitem'
                theme={theme}
                title={account.name}
                onClick={() => selectFromOverflow(account)}>
                {account.name}
              </MenuItem>
            </li>
          ))}
        </Menu>
      )}

      {/* Off-screen width probe; renders the full account list plus a sample
          "+N" so `recompute` can decide how many tabs fit. */}
      <Measurer aria-hidden>
        {order.map((account, i) => (
          <TabButton
            key={keyOf(account)}
            as='div'
            theme={theme}
            ref={el => {
              measureRefs.current[i] = el
            }}>
            {account.name}
          </TabButton>
        ))}
        <OverflowButton as='div' theme={theme} ref={measureOverflowRef}>
          +{order.length}
        </OverflowButton>
      </Measurer>
    </Root>
  )
}

DesktopTabs.propTypes = {
  accounts: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string.isRequired, name: PropTypes.string.isRequired })
  ).isRequired,
  activeId: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  maxVisibleTabs: PropTypes.number,
  className: PropTypes.string,
}

export default DesktopTabs
